/** Money helpers — backend uses integer minor units; UI divides for display only. */
export { formatQtyScaled, qtyScale, parseToQtyScaled } from "./qty";

const DEFAULT_SCALE = 100;

export function moneyScale(): number {
  const raw = import.meta.env.VITE_MONEY_SCALE;
  const n = raw ? Number(raw) : DEFAULT_SCALE;
  return Number.isInteger(n) && n > 0 ? n : DEFAULT_SCALE;
}

function scaleDigits(scale: number): number {
  let digits = 0;
  let n = scale;
  while (n % 10 === 0 && n >= 10) {
    digits += 1;
    n /= 10;
  }
  return digits;
}

/** Display minor units as decimal string (presentation only). */
export function formatMinor(
  minor: number,
  scaleOrLanguage?: number | string,
): string {
  const scale =
    typeof scaleOrLanguage === "number" && Number.isInteger(scaleOrLanguage) && scaleOrLanguage > 0
      ? scaleOrLanguage
      : moneyScale();
  if (!Number.isInteger(minor)) return "0.00";
  const negative = minor < 0;
  const abs = Math.abs(minor);
  const whole = Math.floor(abs / scale);
  const frac = abs % scale;
  const fracStr = String(frac).padStart(scaleDigits(scale), "0");
  return `${negative ? "-" : ""}${whole}.${fracStr}`;
}

/** Backward-compatible alias used by older inputs that expect a decimal string. */
export function fromMinor(minor: number, scale = moneyScale()): string {
  return formatMinor(minor, scale);
}

/** Parse user input / display decimal → integer minor for API payloads. */
export function parseToMinor(
  amount: string | number,
  scale = moneyScale(),
): number {
  const raw = String(amount).trim().replace(/,/g, "");
  if (!raw || raw === "-") throw new Error("amount is empty");

  const negative = raw.startsWith("-");
  const unsigned = negative ? raw.slice(1) : raw;
  if (!/^\d+(\.\d+)?$/.test(unsigned)) {
    throw new Error(`invalid amount: ${amount}`);
  }

  const [wholePart, fracPart = ""] = unsigned.split(".");
  const digits = scaleDigits(scale);
  const fracPadded = (fracPart + "0".repeat(digits)).slice(0, digits);
  if (fracPart.length > digits && fracPart.slice(digits).replace(/0/g, "") !== "") {
    throw new Error(`too many decimal places for scale ${scale}`);
  }

  const minor =
    Number(wholePart) * scale + Number(fracPadded || "0");
  const result = negative ? -minor : minor;
  if (!Number.isSafeInteger(result)) {
    throw new Error("amount exceeds safe integer range");
  }
  return result;
}
